import {
  Avatar,
  Box,
  Button,
  Table,
  Text,
  useUnsafeMemo,
} from '@factorialco/deprecated-design-system'
import {
  AccountMembershipsDocument,
  BankAccountMembershipMembersDocument,
  BankingBankAccountFragment,
  RemoveBankAccountMembershipMemberDocument,
  ResendAccountMembershipInvitationDocument,
} from 'generated/resources/graphql'

import Popover from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { AccountMembershipRow } from './AccountMembershipRow'
import { MembershipAction } from './MembershipActionsMenu'

interface Props {
  bankAccount: BankingBankAccountFragment
  notRemovableEmployeeIds?: string[]
  onAddClick: () => void
  onAddAccountMembershipClick?: () => void
  accountId?: string
  onboardingAuthorEmployeeId?: string
}

// ─────────────────────────────────────────────────────────────────────────────
// AccountMemberships - Swan account memberships with consent flow
// ─────────────────────────────────────────────────────────────────────────────

const AccountMemberships = ({
  accountId,
  onAddClick,
  onboardingAuthorEmployeeId,
}: {
  accountId: string
  onAddClick: () => void
  onboardingAuthorEmployeeId?: string
}) => {
  const { employee } = useCurrentSession()
  const currentEmployeeId = employee.id

  const { data } = useQuery(AccountMembershipsDocument, {
    variables: { accountIds: [accountId] },
  })

  const [resendInvitation, { loading: resending }] = useMutation(
    ResendAccountMembershipInvitationDocument,
    {
      refetchQueries: [AccountMembershipsDocument],
      extractorFn: (response) => response.bankingMutations.resendInvitationBankingAccountMembership,
    }
  )

  const handleAction = async (action: MembershipAction) => {
    switch (action.type) {
      case 'complete_consent':
        window.location.href = action.consentUrl
        break

      case 'resend_invitation':
      case 'retry':
        await handleResendInvitation(action.membershipId)
        break

      case 'complete_setup':
        window.location.href = action.setupUrl
        break
    }
  }

  const handleResendInvitation = async (membershipId: string) => {
    const result = await resendInvitation({ variables: { membershipId } })
    const handled = handleResult(result, (formErrors) => {
      const errorMessage =
        formErrors.message || formErrors.error || Object.values(formErrors).join(', ')
      if (errorMessage) {
        app.setFlash({ type: 'wrong', message: errorMessage })
      }
    })

    if (handled.success && handled.data) {
      const membership = handled.data.accountMembership

      // Check if OAuth authorization is needed first
      if (membership?.authorizationUrl) {
        window.location.href = membership.authorizationUrl
        return
      }

      // Then check for consent URL (Swan SCA)
      if (membership?.status === 'pending_consent' && membership.consent?.consentUrl) {
        window.location.href = membership.consent.consentUrl
        return
      }

      app.setFlash({
        type: 'correct',
        message: i18n.t('banking.account_memberships.actions.invitation_sent'),
      })
    }
  }

  const memberships = data?.banking.accountMemberships ?? []

  return (
    <Box gap={'s4'}>
      <SectionHeader
        title={i18n.t('banking.account_memberships.section.title')}
        subtitle={i18n.t('banking.account_memberships.section.subtitle')}
        onAddClick={onAddClick}
      />
      <Box maxHeight={'s240'} overflowY="auto">
        {memberships.length > 0 && (
          <Table autoLayout>
            <Table.Head>
              <Table.Row>
                <Table.HeadCell colSpan={6}>
                  {i18n.t('banking.account_memberships.table.user')}
                </Table.HeadCell>
                <Table.HeadCell colSpan={3}>
                  {i18n.t('banking.account_memberships.table.added_on')}
                </Table.HeadCell>
                <Table.HeadCell colSpan={2}>
                  {i18n.t('banking.account_memberships.table.status')}
                </Table.HeadCell>
                <Table.HeadCell colSpan={1}></Table.HeadCell>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {memberships.map((membership) => (
                <AccountMembershipRow
                  key={`membership-${membership.id}`}
                  membership={membership}
                  currentEmployeeId={currentEmployeeId}
                  onboardingAuthorEmployeeId={onboardingAuthorEmployeeId}
                  onAction={handleAction}
                  actionDisabled={resending}
                />
              ))}
            </Table.Body>
          </Table>
        )}
      </Box>
    </Box>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// BankAccountMemberships - Internal bank account access (non-Swan)
// ─────────────────────────────────────────────────────────────────────────────

const BankAccountMemberships = ({
  bankAccount,
  notRemovableEmployeeIds,
  onAddClick,
}: {
  bankAccount: BankingBankAccountFragment
  notRemovableEmployeeIds?: string[]
  onAddClick: () => void
}) => {
  const { company } = useCurrentSession()

  const [removeMembership, { loading }] = useMutation(RemoveBankAccountMembershipMemberDocument, {
    refetchQueries: [BankAccountMembershipMembersDocument],
    extractorFn: (data) => data.bankingMutations.upsertBankingBankAccountMembership,
  })

  const { data } = useQuery(BankAccountMembershipMembersDocument, {
    variables: { bankAccountId: bankAccount.id },
  })

  const members = useUnsafeMemo(() => {
    if (!data?.banking.bankAccountMemberships) return []

    const memberMap = new Map()
    data.banking.bankAccountMemberships.forEach((membership) => {
      membership.members?.forEach((member) => {
        memberMap.set(member.id, member)
      })
    })

    return Array.from(memberMap.values())
  }, [data?.banking.bankAccountMemberships])

  const handleRemove = (employeeId: string) => {
    removeMembership({
      variables: {
        employeeIds: [employeeId],
        companyId: company.id,
        bankAccountId: bankAccount.id,
      },
    })
  }

  return (
    <Box gap={'s4'}>
      <SectionHeader
        title={i18n.t('banking.accounts.finish_wizard.step_3.title')}
        subtitle={i18n.t('banking.accounts.finish_wizard.step_3.subtitle')}
        onAddClick={onAddClick}
        addDisabled={loading}
      />
      <Box maxHeight={'s240'} overflowY="auto">
        {bankAccount.bankAccountMembership?.id && (
          <Table autoLayout>
            <Table.Head>
              <Table.Row>
                <Table.HeadCell colSpan={9}>
                  {i18n.t('billing.subscription.plans.expenses.columns.user')}
                </Table.HeadCell>
                <Table.HeadCell colSpan={2}>
                  {i18n.t('billing.subscription.plans.expenses.columns.added_on')}
                </Table.HeadCell>
                <Table.HeadCell colSpan={1}></Table.HeadCell>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {members?.map((member) => (
                <BankAccountMemberRow
                  key={`member-${member.id}`}
                  member={member}
                  canRemove={!notRemovableEmployeeIds?.includes(member.access?.employee?.id)}
                  onRemove={() => handleRemove(member.access?.employee?.id)}
                  removeDisabled={loading}
                />
              ))}
            </Table.Body>
          </Table>
        )}
      </Box>
    </Box>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared components
// ─────────────────────────────────────────────────────────────────────────────

const SectionHeader = ({
  title,
  subtitle,
  onAddClick,
  addDisabled,
}: {
  title: string
  subtitle: string
  onAddClick: () => void
  addDisabled?: boolean
}) => (
  <Box flexDirection="row" justifyContent="spaceBetween" width="full" paddingBottom={'s8'}>
    <Box flexDirection="column" gap={'s4'}>
      <Text.Normal color="grey1000" weight="medium">
        {title}
      </Text.Normal>
      <Text color="grey800" weight="normal" size="200">
        {subtitle}
      </Text>
    </Box>
    <Box flexDirection="row" justifyContent="flexEnd" alignItems="center">
      <Button disabled={addDisabled} type="primary" iconName="add" onClick={onAddClick}>
        {i18n.t('banking.accounts.side_panel.add_membership')}
      </Button>
    </Box>
  </Box>
)

interface MemberType {
  id: number
  createdAt?: string | null
  access?: {
    employee?: {
      id: number
      fullName?: string | null
      avatar?: { url?: string | null } | null
    } | null
  } | null
}

const BankAccountMemberRow = ({
  member,
  canRemove,
  onRemove,
  removeDisabled,
}: {
  member: MemberType
  canRemove: boolean
  onRemove: () => void
  removeDisabled: boolean
}) => (
  <Table.Row>
    <Table.Cell colSpan={9} paddingX="s4">
      <Box flexDirection="row" alignItems="center" gap="s16" minWidth="s96" paddingLeft={'s16'}>
        <Avatar
          size="s40"
          url={buildUrl(member.access?.employee?.avatar?.url)}
          label={member.access?.employee?.fullName || ''}
        />
        <Text.Normal size="200" weight="medium">
          {member.access?.employee?.fullName || ''}
        </Text.Normal>
      </Box>
    </Table.Cell>
    <Table.Cell colSpan={2} paddingX="s4">
      <Text.Normal size="200" weight="medium">
        {member.createdAt
          ? i18nDateToString(stringToDate(member.createdAt, 'backend_datetime'), 'ui')
          : ''}
      </Text.Normal>
    </Table.Cell>
    <Table.Cell colSpan={1} paddingX="s4">
      {canRemove && (
        <Box flexDirection="row" gap="s8" justifyContent="flexEnd" paddingRight={'s16'}>
          <Popover type="list">
            <Popover.Trigger>
              <Button.Decorative type="secondary" iconName="ellipsis" />
            </Popover.Trigger>
            <Popover.Content side="bottom" align="end">
              <Popover.TouchableOption
                onClick={onRemove}
                title={i18n.t('banking.accounts.side_panel.remove_membership')}
                danger
                disabled={removeDisabled}
              />
            </Popover.Content>
          </Popover>
        </Box>
      )}
    </Table.Cell>
  </Table.Row>
)

// ─────────────────────────────────────────────────────────────────────────────
// Main export
// ─────────────────────────────────────────────────────────────────────────────

export const SidepanelMemberships = ({
  bankAccount,
  notRemovableEmployeeIds,
  onAddClick,
  onAddAccountMembershipClick,
  accountId,
  onboardingAuthorEmployeeId,
}: Props) => {
  return (
    <>
      <BankAccountMemberships
        bankAccount={bankAccount}
        notRemovableEmployeeIds={notRemovableEmployeeIds}
        onAddClick={onAddClick}
      />
      {accountId && onAddAccountMembershipClick && (
        <AccountMemberships
          accountId={accountId}
          onAddClick={onAddAccountMembershipClick}
          onboardingAuthorEmployeeId={onboardingAuthorEmployeeId}
        />
      )}
    </>
  )
}
